/**
 * Install affordance state. `PWA-006` through `PWA-010`.
 *
 * `classifyPlatform` is pure and takes its inputs rather than reading
 * `navigator`/`matchMedia` itself, the same split `storage.ts`'s injected
 * `KeyValueStore` and `levels.ts`'s injected `fetchImpl` already use --
 * `PWA-010`'s platform links are a real, testable claim about a UA string,
 * not something that needs a real iPhone (or WebKit) to prove. The signal
 * below is the one place that reads the real globals, at module load, and is
 * exercised by component/E2E tests rather than unit tests of its own.
 */

import { signal } from '@preact/signals';

export type InstallAffordance =
  | { kind: 'prompt' } // beforeinstallprompt fired; a real button can call prompt()
  | { kind: 'link'; href: string; label: string } // iOS/Mac Safari: link to the vendor's own instructions
  | { kind: 'none' }; // already installed (PWA-008), or no known mechanism yet (PWA-009)

const IOS_INSTALL_LINK: InstallAffordance = {
  kind: 'link',
  href: 'https://support.apple.com/guide/iphone/open-as-web-app-iphea86e5236/ios',
  label: 'How to install on iPhone or iPad',
};

const MAC_SAFARI_INSTALL_LINK: InstallAffordance = {
  kind: 'link',
  href: 'https://support.apple.com/en-us/104996',
  label: 'How to install on Mac',
};

/**
 * `PWA-008`: already running installed hides the section entirely, checked
 * first and unconditionally -- an installed iPad is still iOS by user agent,
 * and must not fall through to `PWA-010`'s link. `PWA-010`: iOS and Mac
 * Safari get a link to the platform vendor's own instructions, since neither
 * fires `beforeinstallprompt`. `PWA-009`: anything else defaults to no
 * mechanism, which a real `beforeinstallprompt` event upgrades at runtime
 * (`listenForInstallPrompt`) -- this function only classifies the platforms
 * that have no such event to wait for.
 */
export function classifyPlatform(
  userAgent: string,
  isStandaloneDisplay: boolean,
): InstallAffordance {
  if (isStandaloneDisplay) return { kind: 'none' };
  if (/iPad|iPhone|iPod/.test(userAgent)) return IOS_INSTALL_LINK;
  if (
    userAgent.includes('Macintosh') &&
    userAgent.includes('Safari') &&
    !/Chrome|Chromium|Edg/.test(userAgent)
  ) {
    return MAC_SAFARI_INSTALL_LINK;
  }
  return { kind: 'none' };
}

function detectStandaloneDisplay(): boolean {
  // jsdom (this project's test environment) has no `matchMedia` at all --
  // guarded rather than assumed, the same defensive shape as every other
  // "does this environment actually have the API" check in this file.
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari has no `display-mode` media query support; this is its own
  // vendor-specific signal for the same fact.
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return displayModeStandalone || iosStandalone;
}

export const installAffordance = signal<InstallAffordance>(
  typeof window === 'undefined'
    ? { kind: 'none' }
    : classifyPlatform(navigator.userAgent, detectStandaloneDisplay()),
);

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

/**
 * `PWA-006`: where the browser offers its own install prompt (Chrome, Edge),
 * intercept it and present our own control instead of the browser's -- the
 * event is only usable if `preventDefault()` is called on it synchronously,
 * within this handler.
 */
export function listenForInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    installAffordance.value = { kind: 'prompt' };
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installAffordance.value = { kind: 'none' };
  });
}

export async function promptInstall(): Promise<void> {
  const event = deferredPrompt;
  if (!event) return;
  deferredPrompt = null;
  await event.prompt();
}
